import { Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Box } from '@mui/material';
import { X } from 'lucide-react';
import { type ReactNode } from 'react';
import { colors, radius, spacing, fonts } from '@/theme/tokens';
import { AppButton } from './AppButton';

type AppModalSize = 'sm' | 'md' | 'lg' | 'xl';

interface AppModalProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  children?: ReactNode;
  actions?: ReactNode;
  size?: AppModalSize;
  hideCloseButton?: boolean;
}

const sizeMap: Record<AppModalSize, string> = {
  sm: '400px',
  md: '500px',
  lg: '640px',
  xl: '800px',
};

export function AppModal({
  open,
  onClose,
  title,
  children,
  actions,
  size = 'md',
  hideCloseButton = false,
}: AppModalProps) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          backgroundColor: colors.surface,
          borderRadius: radius.xl,
          border: `1px solid ${colors.border}`,
          // eslint-disable-next-line i18next/no-literal-string -- CSS value, not copy
          boxShadow: '0 20px 40px rgba(31, 41, 51, 0.16)',
          width: '100%',
          maxWidth: sizeMap[size],
          margin: spacing[4],
        },
      }}
      slotProps={{
        backdrop: {
          sx: {
            // eslint-disable-next-line i18next/no-literal-string -- CSS value, not copy
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
          },
        },
      }}
    >
      {(title || !hideCloseButton) && (
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: spacing[6],
            paddingBottom: spacing[4],
            fontFamily: fonts.heading,
            fontWeight: 600,
            fontSize: '20px',
            color: colors.textPrimary,
          }}
        >
          {title}
          {!hideCloseButton && (
            <IconButton
              onClick={onClose}
              size="small"
              sx={{
                color: colors.textMuted,
                '&:hover': {
                  color: colors.textPrimary,
                  backgroundColor: colors.surface,
                },
              }}
            >
              <X size={20} />
            </IconButton>
          )}
        </DialogTitle>
      )}
      <DialogContent
        sx={{
          padding: spacing[6],
          paddingTop: title ? 0 : spacing[6],
          color: colors.textPrimary,
        }}
      >
        {children}
      </DialogContent>
      {actions && (
        <DialogActions
          sx={{
            padding: spacing[6],
            paddingTop: 0,
            gap: spacing[3],
          }}
        >
          {actions}
        </DialogActions>
      )}
    </Dialog>
  );
}

// Confirmation modal variant
interface AppConfirmModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string | ReactNode;
  confirmLabel?: string;
  confirmText?: string; // Alias for confirmLabel
  cancelLabel?: string;
  variant?: 'default' | 'danger';
  loading?: boolean;
  confirmDisabled?: boolean;
}

export function AppConfirmModal({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel,
  confirmText,
  cancelLabel = 'Cancel',
  variant = 'default',
  loading = false,
  confirmDisabled = false,
}: AppConfirmModalProps) {
  const buttonLabel = confirmLabel || confirmText || 'Confirm';

  return (
    <AppModal open={open} onClose={onClose} title={title} size="sm">
      <Box sx={{ color: colors.textMuted, marginBottom: spacing[6] }}>{message}</Box>
      <Box sx={{ display: 'flex', gap: spacing[3], justifyContent: 'flex-end' }}>
        <AppButton variant="ghost" onClick={onClose} disabled={loading}>
          {cancelLabel}
        </AppButton>
        <AppButton
          variant={variant === 'danger' ? 'danger' : 'primary'}
          onClick={onConfirm}
          loading={loading}
          disabled={confirmDisabled}
        >
          {buttonLabel}
        </AppButton>
      </Box>
    </AppModal>
  );
}
